import React, { FunctionComponent, MutableRefObject, useState, useRef } from "react";
import { Theme } from "@emotion/react";
import { InteractiveElement } from "@iventis/styles/src/components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { styled } from "@iventis/styles";
import { useTheme } from "@mui/material";
import { Body2, ButtonText } from "@iventis/styles/src/components/texts";
import { Content } from "@iventis/translations/content/typed-content";
import { DisabledOverlay, ReadonlyToolTipComponent, StyledPopover } from "@iventis/components";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { DataTableCallbackRefs } from "@iventis/data-table";
import { UnitOfMeasurement } from "@iventis/domain-model/model/unitOfMeasurement";
import { useFunctionality } from "@iventis/utilities";
import { SelectedMapObject } from "@iventis/map-types";
import { ExportType, createMapObjectExportNotification, notificationNameCreator, useNotificationContext } from "@iventis/notifications";
import { useSelector } from "react-redux";
import { MapObjectRowNodeData } from "./sidebar-data-table.types";
import { SidebarButton } from "../sidebar-button";
import { exportDataTable } from "./sidebar-data-table-helpers";
import { RootState } from "../state/root.store";

export const DataTableOperationsComponent: FunctionComponent<{
    dropdown?: boolean;
    disabled?: boolean;
    mapObjectsSelected?: SelectedMapObject[];
    canUserEdit?: boolean;
    gridRef: MutableRefObject<DataTableCallbackRefs<MapObjectRowNodeData>>;
    mapId: string;
    layerIds: string[];
    unitOfMeasurement: UnitOfMeasurement;
    columnIdsToNameMapping: { [key: string]: string };
    columnsDisplayed: string[];
}> = ({ dropdown, mapObjectsSelected, disabled, canUserEdit, gridRef, mapId, layerIds, unitOfMeasurement, columnIdsToNameMapping, columnsDisplayed }) => {
    const translate = useIventisTranslate();

    const functionality = useFunctionality();

    const { userId, projectId, mapName } = useSelector((state: RootState) => ({
        userId: state.auth.user.userId,
        projectId: state.auth.user.projectId,
        mapName: state.mapReducer.mapName,
    }));

    const { createNotification } = useNotificationContext();

    const theme = useTheme<Theme>();

    const [popover, setPopover] = useState<"export" | null>(null);
    const exportButtonRef = useRef<HTMLButtonElement>();

    const onDelete = () => gridRef.current?.deleteRequest(false);

    const exportMapObjects = (exportSelectionOnly = false) => {
        const mapObjectExport = exportDataTable(
            gridRef.current,
            mapId,
            layerIds,
            columnIdsToNameMapping,
            unitOfMeasurement,
            columnsDisplayed,
            exportSelectionOnly ? mapObjectsSelected.map(({ objectId }) => objectId) : undefined
        );

        const exportNotification = createMapObjectExportNotification(userId, projectId, notificationNameCreator(ExportType.LayerExcel, mapName), mapObjectExport);
        createNotification(exportNotification);

        if (popover != null) {
            setPopover(null);
        }
    };

    const onExport = () => {
        if (mapObjectsSelected?.length > 1) {
            setPopover("export");
        } else {
            exportMapObjects();
        }
    };

    return (
        <>
            {dropdown ? (
                <>
                    <InteractiveElement onClick={onDelete} className="button" type="button" disabled={disabled || mapObjectsSelected.length === 0}>
                        {disabled && <DisabledOverlay />}
                        <ButtonText>{translate(Content.common.buttons.delete)}</ButtonText>
                    </InteractiveElement>
                    <InteractiveElement onClick={onExport} className="button" type="button" disabled={disabled}>
                        {disabled && <DisabledOverlay />}
                        <ButtonText>{translate(Content.common.buttons.export)}</ButtonText>
                    </InteractiveElement>
                </>
            ) : (
                <StyledOperations className="operations">
                    <SidebarButton
                        onClick={onDelete}
                        canUserEdit={canUserEdit}
                        disable={disabled || mapObjectsSelected.length === 0}
                        toolTip={translate(Content.common.buttons.delete)}
                        toolTipPermission={<ReadonlyToolTipComponent resource="layer" />}
                    >
                        <FontAwesomeIcon icon={["far", "trash"]} />
                    </SidebarButton>
                    <SidebarButton
                        onClick={() => {
                            if (functionality.attributeDataExport) onExport();
                            else functionality.open("attributeDataExport");
                        }}
                        disable={disabled}
                        toolTip={translate(Content.common.buttons.export)}
                        ref={exportButtonRef}
                        testId="data-export-button"
                    >
                        <FontAwesomeIcon color={functionality.attributeDataExport ? "inherit" : theme.primaryColors.focus} icon={["fas", "file-arrow-down"]} />
                    </SidebarButton>
                    <StyledPopover
                        space={44}
                        anchorOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                        }}
                        open={popover === "export"}
                        anchorEl={exportButtonRef.current}
                        onClose={() => setPopover(null)}
                    >
                        {(["Export all", "Export selected rows only"] as const).map((choice) => (
                            <InteractiveElement key={choice} className="button" type="button" onClick={() => exportMapObjects(choice === "Export selected rows only")}>
                                <Body2>{translate(Content.table[choice])}</Body2>
                            </InteractiveElement>
                        ))}
                    </StyledPopover>
                </StyledOperations>
            )}
        </>
    );
};

const StyledOperations = styled.div`
    display: flex;
    align-items: center;
    height: 100%;
    button {
        height: 100%;
        width: ${({ theme }: { theme: Theme }) => theme.sizes.iconButtonWidth};
        color: ${({ theme }: { theme: Theme }) => theme.primaryColors.subdued70};
        :hover {
            color: ${({ theme }: { theme: Theme }) => theme.primaryColors.focusAccent};
        }
        :disabled {
            color: #9394a0;
        }
    }

    .border {
        width: 0.5px;
        height: 60%;
        background-color: ${({ theme }: { theme: Theme }) => theme.typographyColors.subdued};
    }
`;
